
import { ReactNode, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { AuthContext } from "./AuthContext";
import { useAuthentication } from "@/hooks/auth/useAuthentication";
import { useProfile } from "@/hooks/auth/useProfile";
import { clearLocationCache } from "@/utils/geo-location";

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const {
    user,
    setUser,
    session,
    setSession,
    isLoading,
    setIsLoading,
    signIn,
    signUp,
    signOut,
  } = useAuthentication();

  const { profile, setProfile, refreshProfile: fetchProfileData } = useProfile();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Reference to track last refresh time to debounce requests
  const lastRefreshTimeRef = useRef<number>(0);
  const debounceTimeMs = 5000; // 5 seconds debounce
  const refreshInProgressRef = useRef<boolean>(false);

  // Enhanced sign out function to clear location cache
  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      // Clear location cache when signing out
      clearLocationCache();
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  }, [signOut]);

  // Function to refresh the profile - exposed to components
  const refreshProfile = useCallback(async () => {
    if (!user) return false;
    
    // Check if we've refreshed recently (debounce)
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < debounceTimeMs) {
      console.log("Profile refresh debounced - skipping (refreshed recently)");
      return false;
    }
    
    // Prevent concurrent refreshes
    if (isRefreshing || refreshInProgressRef.current) {
      console.log("Profile refresh already in progress - skipping");
      return false;
    }
    
    try {
      setIsRefreshing(true);
      refreshInProgressRef.current = true;
      lastRefreshTimeRef.current = now;
      console.log("Refreshing user profile from AuthProvider");
      
      const result = await fetchProfileData(user);
      
      // Reload the session to get most up-to-date auth state
      // Only if profile fetch succeeded to avoid unnecessary requests
      if (result) {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setSession(data.session);
        }
      }
      
      setIsRefreshing(false);
      refreshInProgressRef.current = false;
      return !!result;
    } catch (error) {
      console.error("Error refreshing profile:", error);
      setIsRefreshing(false);
      refreshInProgressRef.current = false;
      return false;
    }
  }, [user, fetchProfileData, setSession, isRefreshing]);

  // Auth state change listener
  useEffect(() => {
    setIsLoading(true);

    // Get the initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user && session.user.email_confirmed_at) {
        // Only fetch profile for confirmed users
        fetchProfileData(session.user)
          .then(() => {
            // Check for pending demo request after confirmation
            const pendingDemoRequest = sessionStorage.getItem('pendingDemoRequest');
            if (pendingDemoRequest) {
              import("@/services/demoRequestService").then(({ saveDemoRequestToDatabase }) => {
                saveDemoRequestToDatabase(session.user, pendingDemoRequest)
                  .then(() => {
                    sessionStorage.removeItem('pendingDemoRequest');
                    console.log("Pending demo request saved after confirmation");
                  })
                  .catch(error => console.error("Error saving pending demo request:", error));
              });
            }
          })
          .catch(error => console.error("Error fetching profile:", error))
          .finally(() => setIsLoading(false));
      } else {
        // Clear location cache when no confirmed session
        clearLocationCache();
        setIsLoading(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user && session.user.email_confirmed_at) {
          // Only fetch profile for confirmed users
          fetchProfileData(session.user).catch(
            error => console.error("Error fetching profile on auth change:", error)
          );
        } else {
          // If user signed out or session expired, clear profile and location cache
          if (event === 'SIGNED_OUT') {
            clearLocationCache();
          }
          setProfile(null);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setSession, setIsLoading, fetchProfileData, setProfile]);

  const contextValue = {
    user,
    session,
    profile,
    isLoading,
    signIn,
    signUp,
    signOut: handleSignOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
